import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ReactNode } from 'react';

import Inline from '../../../components/Inline/Inline';
import WrapIf from '../../../components/WrapIf/WrapIf';
import useTrans from '../../../hooks/ui/useTrans';
import LoggedInCustomer from '../../types/LoggedInCustomer';
import LocaleSwitch from '../LocaleSwitch/LocaleSwitch';
import LoginAndForgotPasswordCard from '../LoginAndForgotPasswordCard/LoginAndForgotPasswordCard';
import OverlayHeaderButton from '../OverlayHeaderButton/OverlayHeaderButton';

import closeIcon from './graphics/close.svg?raw';
import loginIcon from './graphics/login.svg?raw';
import useLoginForm from './hooks/useLoginForm';
import HeaderOptions from './types/HeaderOptions';

import styles from './LayoutHeader.module.css';

type LayoutHeaderProps = {
    loggedInCustomer: LoggedInCustomer | undefined;
    children?: ReactNode;
    inlineLoginUrl?: string;
    inlineRequestPasswordResetUrl?: string;
    onClose?: () => void;
    options?: HeaderOptions;
};

const LayoutHeader = ({
    loggedInCustomer,
    children,
    inlineLoginUrl,
    inlineRequestPasswordResetUrl,
    options,
    onClose,
}: LayoutHeaderProps) => {
    const trans = useTrans('CustomerArea.Shared.OverlayHeader.');
    const login = useLoginForm({
        loggedInCustomer: loggedInCustomer,
        enabled: !!inlineLoginUrl,
        defaultDisplay: options?.defaultLoginFormDisplay,
    });

    const determineLoginModalCloseHandler = () => {
        if (login.display === 'force') {
            if (onClose) {
                return onClose;
            }

            return undefined;
        }

        return login.hide;
    };

    const isShowingLoginCard = !!inlineLoginUrl && login.display === 'optional';

    return (
        <div
            className={classNames(
                styles.Root,
                isShowingLoginCard && styles.bottomSpacing
            )}
        >
            <WrapIf condition={!!options?.wrapper} wrapper={options?.wrapper}>
                <div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 1,
                            transition: {
                                duration: 0.2,
                                delay: 0.5,
                            },
                        }}
                    >
                        <Inline
                            horizontal="right"
                            space="none"
                            className={styles.Buttons}
                        >
                            {children}

                            {!loggedInCustomer &&
                                !options?.preventLoginOrLogout &&
                                options?.inlineLogin &&
                                inlineLoginUrl && (
                                    <OverlayHeaderButton
                                        icon={loginIcon}
                                        onClick={login.toggle}
                                    >
                                        {trans('LogInButton')}
                                    </OverlayHeaderButton>
                                )}

                            {!options?.hideLanguageSwitch && <LocaleSwitch />}

                            {onClose && (
                                <OverlayHeaderButton
                                    icon={closeIcon}
                                    onClick={onClose}
                                >
                                    {trans('CloseButton')}
                                </OverlayHeaderButton>
                            )}
                        </Inline>
                    </motion.div>

                    {inlineLoginUrl && !options?.preventLoginOrLogout && (
                        <LoginAndForgotPasswordCard
                            onClose={determineLoginModalCloseHandler()}
                            onReady={login.hide}
                            loginUrl={inlineLoginUrl}
                            requestPasswordResetUrl={
                                inlineRequestPasswordResetUrl
                            }
                            display={login.display}
                        />
                    )}
                </div>
            </WrapIf>
        </div>
    );
};

export default LayoutHeader;
